import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Tooltip,
  Filler,
  Plugin,
  Chart as ChartType,
  ChartEvent,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { useTheme } from "../context/ThemeContext";
import candlestickPlugin from "../utils/candlestickPlugin";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarController,
  BarElement,
  Tooltip,
  Filler,
);

// Concrete colors, NOT CSS variables: canvas strokeStyle can't resolve var(--x) and
// silently falls back to black (same constraint as ForwardCurveFanChart).
const QUERY_DARK = "#e9ecef";
const QUERY_LIGHT = "#212529";
const UP_COLOR = "#198754";
const DOWN_COLOR = "#dc3545";
const DIVIDER_DARK = "#6c757d";
const DIVIDER_LIGHT = "#adb5bd";

export type GameChartMode = "line" | "candles";

interface Props {
  /** Z-normalized query closes — the part the player judges. */
  series: number[];
  /** Same bars as [o,h,l,c] on the same scale; null when the round has no candle view. */
  ohlc?: number[][] | null;
  /** Realized continuation; only passed after the player has answered. */
  forward?: number[] | null;
  /** Candle form of the continuation. */
  forwardOhlc?: number[][] | null;
  /** Direction actually realized, which colors the continuation. */
  realized?: GameDirection | null;
  mode?: GameChartMode;
  /**
   * Bars of empty space to hold open right of the divider before the reveal, so the chart
   * shows the gap the player is being asked to fill.
   */
  forwardPoints?: number;
  /** When set, the empty right side of the chart becomes clickable up/down zones. */
  onCall?: (direction: GameDirection) => void;
  height?: number;
}

/**
 * Everything the canvas plugins need to draw, held in a ref rather than passed in.
 *
 * react-chartjs-2 reads its `plugins` prop only when it CONSTRUCTS the chart, so handing it
 * a freshly-built plugin array on re-render is a no-op — the live chart keeps the closures it
 * was born with. Hence one stable plugin instance per chart, reading current values through
 * this ref (which also means hovering redraws without a React render).
 */
interface ZoneState {
  dividerIndex: number;
  dark: boolean;
  hover: GameDirection | null;
  /** False once the round is answered — the prompt must stop painting over the reveal. */
  active: boolean;
}

/**
 * Draws the "decision point" divider where the known history ends. Its position is the
 * whole point of the chart, so it's drawn even before the reveal.
 */
function buildDividerPlugin(state: React.RefObject<ZoneState>): Plugin {
  return {
    id: "gameDivider",
    afterDatasetsDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      const xScale = scales.x;
      // chartArea/scales are undefined until the first layout pass despite the types.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!chartArea || !xScale) return;
      const x = xScale.getPixelForValue(state.current.dividerIndex);
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = state.current.dark ? DIVIDER_DARK : DIVIDER_LIGHT;
      ctx.lineWidth = 1;
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
      ctx.restore();
    },
  };
}

/** Smallest usable call zone; a 1-day horizon reserves ~1 bar, far too thin to hit. */
const MIN_ZONE_PX = 88;

/**
 * Left edge of the call zone. Normally the divider, but widened leftward over the tail of
 * the history when the reserved forward space is too narrow to click — the zone's job is to
 * be hittable, and the divider stays drawn where it truly is.
 */
function callZoneLeft(chart: ChartType, index: number): number | null {
  const { chartArea, scales } = chart;
  // chartArea/scales are undefined until the first layout pass despite the types.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!chartArea || !scales.x) return null;
  const divider = scales.x.getPixelForValue(index);
  return Math.max(chartArea.left, Math.min(divider, chartArea.right - MIN_ZONE_PX));
}

/**
 * Paints the call to action into the space right of the divider: a faint split line with
 * ▲/▼ prompts, and a tint on whichever half the cursor is over.
 *
 * The affordance is drawn on the canvas rather than in an overlay div because only the
 * chart knows where the divider actually is — `chartArea`/`scales` give exact geometry,
 * while a positioned div would have to re-derive it from the category scale's padding and
 * would drift out of alignment at other sizes.
 */
function buildCallZonePlugin(state: React.RefObject<ZoneState>): Plugin {
  return {
    id: "gameCallZone",
    afterDatasetsDraw(chart) {
      const { hover, dark, active, dividerIndex } = state.current;
      if (!active) return;
      const { ctx, chartArea } = chart;
      const left = callZoneLeft(chart, dividerIndex);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (left === null || !chartArea) return;
      const { right, top, bottom } = chartArea;
      const mid = (top + bottom) / 2;

      ctx.save();
      if (hover) {
        ctx.fillStyle = hover === "up" ? "rgba(25, 135, 84, 0.13)" : "rgba(220, 53, 69, 0.13)";
        ctx.fillRect(left, hover === "up" ? top : mid, right - left, (bottom - top) / 2);
      }
      ctx.strokeStyle = dark ? "rgba(233, 236, 239, 0.15)" : "rgba(33, 37, 41, 0.15)";
      ctx.setLineDash([3, 5]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(left, mid);
      ctx.lineTo(right, mid);
      ctx.stroke();

      ctx.setLineDash([]);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "600 16px system-ui, sans-serif";
      const cx = (left + right) / 2;
      ctx.fillStyle = hover === "up" ? UP_COLOR : "rgba(25, 135, 84, 0.5)";
      ctx.fillText("▲", cx, top + (mid - top) / 2);
      ctx.fillStyle = hover === "down" ? DOWN_COLOR : "rgba(220, 53, 69, 0.5)";
      ctx.fillText("▼", cx, mid + (bottom - mid) / 2);
      ctx.restore();
    },
  };
}

/**
 * GameChart — the blind chart, and after the reveal the realized continuation drawn past
 * the divider.
 *
 * Axis ticks are hidden on purpose. The series is z-normalized, so the numbers carry no
 * meaning to read, and any date or price label would turn the game into a lookup exercise.
 *
 * The x-axis reserves the forward bars from the start, so answering fills the empty space
 * instead of rescaling the chart under the player — and that reserved space is what the
 * ▲/▼ call zones live in.
 */
export default function GameChart({
  series,
  ohlc,
  forward,
  forwardOhlc,
  realized,
  mode = "line",
  forwardPoints = 0,
  onCall,
  height = 320,
}: Props) {
  const { theme } = useTheme();
  const dark = theme === "dark";
  const queryColor = dark ? QUERY_DARK : QUERY_LIGHT;
  const forwardColor = realized === "down" ? DOWN_COLOR : UP_COLOR;
  const dividerIndex = series.length - 1;
  const candles = mode === "candles" && ohlc ? ohlc : null;
  const fwdLen = forward?.length ?? 0;
  const total = series.length + Math.max(fwdLen, forwardPoints);

  // Mutated, never re-created: see ZoneState. Kept current on every render.
  const zoneState = useRef<ZoneState>({ dividerIndex, dark, hover: null, active: Boolean(onCall) });
  zoneState.current.dividerIndex = dividerIndex;
  zoneState.current.dark = dark;
  zoneState.current.active = Boolean(onCall);
  if (!onCall) zoneState.current.hover = null;

  const { labels, datasets, bounds } = useMemo(() => {
    const fwd = forward ?? [];
    const lbls = Array.from({ length: total }, (_, i) => String(i));
    const pad = <T,>(arr: T[]): (T | null)[] => [
      ...arr,
      ...(Array(Math.max(0, total - arr.length)).fill(null) as null[]),
    ];

    if (candles) {
      const bars = [...candles, ...(forwardOhlc ?? [])];
      const extremes = bars.flatMap(b => [b[1], b[2]]);
      return {
        labels: lbls,
        bounds: { min: Math.min(...extremes), max: Math.max(...extremes) },
        datasets: [
          {
            type: "bar" as const,
            label: "OHLC",
            // The bars themselves are invisible — candlestickPlugin paints the wicks and
            // bodies from `candleData`; `data` only drives layout.
            data: pad(bars.map(b => b[3])),
            candleData: pad(bars.map(b => ({ o: b[0], h: b[1], l: b[2], c: b[3] }))),
            backgroundColor: "transparent",
            borderWidth: 0,
          },
        ] as unknown as Record<string, unknown>[],
      };
    }

    // The continuation starts ON the last known point so the two lines join seamlessly.
    const forwardData = fwd.length
      ? pad([...(Array(series.length - 1).fill(null) as null[]), series[series.length - 1], ...fwd])
      : [];

    const sets: Record<string, unknown>[] = [
      {
        type: "line" as const,
        label: "History",
        data: pad(series),
        borderColor: queryColor,
        backgroundColor: "transparent",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.15,
        spanGaps: false,
      },
    ];
    if (fwd.length) {
      sets.push({
        type: "line" as const,
        label: "What happened next",
        data: forwardData,
        borderColor: forwardColor,
        backgroundColor: "transparent",
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0.15,
        spanGaps: false,
      });
    }
    return { labels: lbls, datasets: sets, bounds: null };
  }, [series, forward, candles, forwardOhlc, queryColor, forwardColor, total]);

  // Which call zone a pointer position falls in — null left of the divider, or once the
  // round is answered. Derived from the live scales, so it always matches the painted zones.
  const zoneAt = useCallback(
    (chart: ChartType, x: number, y: number): GameDirection | null => {
      const { chartArea } = chart;
      const left = callZoneLeft(chart, dividerIndex);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!onCall || left === null || !chartArea) return null;
      if (x < left) return null;
      if (y < chartArea.top || y > chartArea.bottom) return null;
      return y < (chartArea.top + chartArea.bottom) / 2 ? "up" : "down";
    },
    [onCall, dividerIndex],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 400 },
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: {
        x: { display: false, grid: { display: false } },
        // Candle wicks reach outside the dataset's own values, so the range comes from the
        // highs/lows rather than being left to autoscale (which would clip them).
        y: {
          display: false,
          grid: { display: false },
          ...(bounds
            ? {
              min: bounds.min - (bounds.max - bounds.min) * 0.08,
              max: bounds.max + (bounds.max - bounds.min) * 0.08,
            }
            : {}),
        },
      },
      onHover: (e: ChartEvent, _els: unknown[], chart: ChartType) => {
        const next = zoneAt(chart, e.x ?? -1, e.y ?? -1);
        const canvas = e.native?.target as HTMLCanvasElement | undefined;
        if (canvas?.style) canvas.style.cursor = next ? "pointer" : "default";
        if (zoneState.current.hover === next) return;
        zoneState.current.hover = next;
        chart.draw(); // repaint straight from the ref — no React render for a hover
      },
      onClick: (e: ChartEvent, _els: unknown[], chart: ChartType) => {
        const zone = zoneAt(chart, e.x ?? -1, e.y ?? -1);
        if (zone && onCall) onCall(zone);
      },
    }),
    [bounds, zoneAt, onCall],
  );

  // One instance for the life of the chart (see ZoneState). candlestickPlugin can still be
  // added conditionally: switching modes changes `type`, which recreates the chart anyway.
  const [basePlugins] = useState<Plugin[]>(() => [
    buildDividerPlugin(zoneState),
    buildCallZonePlugin(zoneState),
  ]);
  const plugins = useMemo(
    () => (candles ? [...basePlugins, candlestickPlugin] : basePlugins),
    [candles, basePlugins],
  );

  return (
    <div style={{ height }}>
      <Chart
        type={candles ? "bar" : "line"}
        data={{ labels, datasets } as never}
        options={options as never}
        plugins={plugins}
      />
    </div>
  );
}
